import { inject, Injectable } from '@angular/core';

import { Capacitor } from '@capacitor/core';
import { CameraPreview, type CameraPreviewOptions } from '@capacitor-community/camera-preview';

import { Platform } from '@ionic/angular';

const CAPTURE_QUALITY = 85;
const CAPTURE_MAX_DIMENSION = 800;
const CAPTURE_MAX_BASE64_SIZE = 80_000;
const LAYOUT_DELAY_MS = 100;

@Injectable()
export class PhotoService {
  private readonly platform = inject(Platform);

  private _previewSize: { width: number; height: number } | null = null;
  private _isRunning = false;

  get previewSize(): { width: number; height: number } | null {
    return this._previewSize;
  }

  async openCamera(parentElementId: string = 'cameraPreview'): Promise<void> {
    const isNative = Capacitor.isNativePlatform();

    // Wait for layout to complete on native
    if (isNative) {
      await new Promise((resolve) => setTimeout(resolve, LAYOUT_DELAY_MS));
    }

    let options: CameraPreviewOptions;

    if (isNative && this.platform.is('ios')) {
      const parentElement = document.getElementById(parentElementId);

      if (parentElement) {
        // Use the parent wrapper (flex container) for dimensions since the element itself may be empty
        const wrapper = parentElement.parentElement;
        const targetElement = wrapper || parentElement;
        const rect = targetElement.getBoundingClientRect();

        // Get iOS safe area top (status bar + notch/Dynamic Island)
        const safeAreaTop = parseInt(
          getComputedStyle(document.documentElement).getPropertyValue('--ion-safe-area-top') || '0',
          10,
        );

        // Get screen width for full-width camera
        const screenWidth = window.screen.width;

        // Use full screen width, height from container
        const width = screenWidth;
        const height = Math.round(rect.height);

        // Camera plugin positions relative to full screen, but webview content
        // is offset by safe area. Add safe area + extra offset for correct position.
        const extraOffset = 100; // Additional offset to separate from top content
        const x = 0; // Edge to edge
        const y = Math.round(rect.top) + safeAreaTop + extraOffset;

        options = {
          position: 'rear',
          parent: parentElementId,
          disableAudio: true,
          x,
          y,
          width,
          height,
          toBack: false,
        };
      } else {
        options = {
          position: 'rear',
          parent: parentElementId,
          disableAudio: true,
          toBack: false,
        };
      }
    } else if (this.platform.is('android')) {
      const parentElement = document.getElementById(parentElementId);

      if (parentElement) {
        const wrapper = parentElement.parentElement;
        const targetElement = wrapper || parentElement;
        const rect = targetElement.getBoundingClientRect();

        const screenWidth = window.screen.width;
        const width = screenWidth;
        const height = Math.round(rect.height);
        const x = 0;
        const y = Math.round(rect.top);

        options = {
          position: 'rear',
          parent: parentElementId,
          disableAudio: true,
          x,
          y,
          width,
          height,
          toBack: false,
        };
      } else {
        options = {
          position: 'rear',
          parent: parentElementId,
          disableAudio: true,
          toBack: false,
        };
      }
    } else {
      // Web
      options = {
        position: 'rear',
        parent: parentElementId,
        disableAudio: true,
        toBack: false,
      };
    }

    this._previewSize = options.width && options.height ? { width: options.width, height: options.height } : null;
    await CameraPreview.start(options);
    this._isRunning = true;
  }

  async closeCamera(): Promise<void> {
    this._isRunning = false;
    const video = document.getElementById('video') as HTMLVideoElement | null;
    if (video?.srcObject) {
      (video.srcObject as MediaStream).getTracks().forEach((track) => track.stop());
      video.remove();
    }
    await CameraPreview.stop().catch(() => {});
  }

  async captureCamera(): Promise<string> {
    const result = await CameraPreview.capture({ quality: CAPTURE_QUALITY });
    const base64 = result.value;

    // On native, resize to reduce payload size (server body-parser limit)
    if (Capacitor.isNativePlatform()) {
      return this.resizeBase64Image(base64, CAPTURE_MAX_DIMENSION, CAPTURE_QUALITY);
    }
    return base64;
  }

  private resizeBase64Image(base64: string, maxDimension: number, quality: number): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;

        if (width > maxDimension || height > maxDimension) {
          const ratio = Math.min(maxDimension / width, maxDimension / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0, width, height);

        // Progressively lower quality until under size limit
        let currentQuality = quality / 100;
        let result: string;
        do {
          const dataUrl = canvas.toDataURL('image/jpeg', currentQuality);
          result = dataUrl.replace('data:image/jpeg;base64,', '');
          currentQuality -= 0.1;
        } while (result.length > CAPTURE_MAX_BASE64_SIZE && currentQuality > 0.1);

        resolve(result);
      };
      img.onerror = reject;
      img.src = `data:image/jpeg;base64,${base64}`;
    });
  }
}
